<template>
  <div v-if="getFilters?.length" class="selected-filters">
    <Chip
      v-for="({ key, value }, index) in getFilters"
      :key="key + index"
      class="selected"
      @click="resetFilter(key, value)"
    >
      <div class="flex items-center gap-1">
        <span>{{ getTranslation(key, value) }}</span>
        <TrailingIcon class="text-primary" />
      </div>
    </Chip>
  </div>
</template>

<script setup type="ts">
import dayjs from 'dayjs'
import TrailingIcon from '~/assets/icons/trailing-icon.svg?component'
import { formatMoney } from '~/utils/currencyHelpers'

const { t } = useI18n()
const emit = defineEmits(['filtersUpdated'])
const productsFilters = inject('productsFilters')

const props = defineProps({
  locationLabel: {
    type: String,
    required: false,
    default: undefined,
  },
})

// const resetAllFilters = () => {
//   productsFilters.value = {}
//   emit('filtersUpdated', productsFilters.value)
// }

const resetFilter = (filterKey, filterValue) => {
  switch (filterKey) {
    case 'price':
      delete productsFilters.value.priceMax
      delete productsFilters.value.priceMin
      break
    case 'dates':
      productsFilters.value.dates = []
      break
    case 'languages':
      productsFilters.value.languages = productsFilters.value.languages.filter(
        (language) => language !== filterValue,
      )
      break
    case 'categories':
      productsFilters.value.categories = []
      break
    default:
      productsFilters.value[filterKey] = undefined
      break
  }

  emit('filtersUpdated', productsFilters.value)
}

// get formatted date
const getDate = (date) => {
  return dayjs(date).format('DD MMM')
}

// get translated label for filters
const getTranslation = (key, value) => {
  if (!value) return

  if (key === 'price') {
    const price = value.split(',')
    return `${formatMoney(price[0])}—${formatMoney(price[1])}`
  }

  if (key === 'dates') {
    const dates = value.split(',')
    return dates[0] !== dates[1] ? `${getDate(dates[0])}—${getDate(dates[1])}` : `${getDate(dates[0])}`
  }

  if (key === 'location') {
    return props.locationLabel || value
  }

  if (key === 'categories') {
    // value return number of categories when select more than 1 category
    // and return label of category when select only 1 category
    return isNaN(Number(value)) ? value : t('search.filters.categories', { n: value })
  }

  const dict = {
    online: t('search.filters.onlineEvents'),
    publicEvents: t('search.filters.publicTickets'),
    directEvents: t('search.filters.privateGroups'),
    hasPromotion: t('search.selectFiltersPromotions.label'),
    'de_DE': 'Deutsch',
    'en_US': 'English',
  }
  return dict[key] || dict[value] || value
}

/**
 * Transform selected filters to array of key => value.
 *
 * @return {Array} a panel containing chart & filter.
 * @param {string} key - The key of selected filter (location, category, language, etc.).
 * @param {string} value - The value of selected filter.
 */
const getFilters = computed(() => {
  const filters = []

  Object.entries(productsFilters.value).forEach(([key, value]) => {
    if (key === 'location' || key === 'locationType') return // we don't show location/locationType in selected filters

    if (value === undefined || !value || value === '' || value === null) { // skip empty values if exists
      return
    }
    if (key === 'priceMin' || key === 'priceMax' || key === 'categories' || key === 'hasPromotion') { // will add after loop
      return
    }

    if (Array.isArray(value)) { // check if value is array
      if (key === 'dates' && value.length === 2) { // dates has only 2 values
        filters.push({ key, value: `${value[0]},${value[1]}` })
      } else { // push everything else separately, like categories
        value.forEach(item => filters.push({ key, value: item }))
      }
    } else { // push single value
      filters.push({ key, value })
    }
  })

  if (productsFilters?.value?.categories?.length) {
    if (productsFilters?.value?.categories?.length === 1) {
      filters.push({ key: 'categories', value: `${productsFilters?.value?.categories}` })
    } else {
      filters.push({ key: 'categories', value: `${productsFilters?.value?.categories?.length}` })
    }
  }

  // add price
  if (productsFilters.value.priceMin !== undefined && productsFilters.value.priceMax !== undefined) {
    filters.push({ key: 'price', value: `${productsFilters.value.priceMin},${productsFilters.value.priceMax}` })
  }

  return filters
})
</script>

<style lang="postcss" scoped>
.selected-filters {
  @apply flex gap-1 whitespace-nowrap md:border-l md:ml-2 md:pl-2 md:flex-wrap;
  border-color: #dddfe4;
}

.selected {
  @apply bg-teal-50 relative hidden md:flex flex-row items-center justify-center text-xs m-0 !border-teal-900 text-primary;
  padding: 6px 10px 6px 14px;
  box-shadow: 0 4px 16px 0 rgba(31, 42, 55, 0.03);
  font-weight: 700;

  &--reset {
    @apply bg-white flex;
  }
}
</style>
